/**
 * TenantContext.tsx
 *
 * Manages the active tenant globally across the app.
 *
 * The context value is intentionally flat — just `tenantId` and `setTenantId`
 * — so consumer components can subscribe to exactly what they need without
 * triggering unnecessary re-renders.
 *
 * Whenever `setTenantId` is called it also synchronises the module-level
 * `currentTenant` variable in api/client.ts so that every subsequent API call
 * automatically uses the new tenant without any additional wiring.
 *
 * Usage:
 *   // Wrap the app once (see main.tsx)
 *   <TenantProvider initialTenantId="t1"><App /></TenantProvider>
 *
 *   // Read / switch tenant inside any component
 *   const { tenantId, setTenantId } = useTenant()
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { setTenant } from '../api/client'

// ── Types ─────────────────────────────────────────────────────────────────────

export type TenantContextValue = {
  /** The currently active tenant ID — injected as X-Tenant-Id on every API call. */
  tenantId: string
  /**
   * Switch the active tenant. This immediately:
   *   1. Updates React state (triggers re-render of subscribed components)
   *   2. Updates the api/client module variable (all subsequent fetches pick it up)
   */
  setTenantId: (id: string) => void
}

// ── Context ───────────────────────────────────────────────────────────────────

const TenantContext = createContext<TenantContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

type TenantProviderProps = {
  /** The tenant to use on first render. Defaults to 'dev-tenant'. */
  initialTenantId?: string
  children: ReactNode
}

export function TenantProvider({ initialTenantId = 'dev-tenant', children }: TenantProviderProps) {
  const [tenantId, setTenantState] = useState<string>(() => {
    // Prefer a persisted value from localStorage so a page refresh does not
    // reset the tenant selection.
    const stored = localStorage.getItem('mc:tenantId')
    return stored ?? initialTenantId
  })

  const setTenantId = useCallback((id: string) => {
    // 1. Persist choice across page refreshes.
    localStorage.setItem('mc:tenantId', id)
    // 2. Update the API layer so the next fetch uses the new tenant.
    setTenant(id)
    // 3. Trigger React re-render.
    setTenantState(id)
  }, [])

  // Sync the API layer on first render in case we loaded from localStorage.
  // (useState initialiser above runs synchronously before any fetch, but this
  //  is a belt-and-braces guard.)
  useMemo(() => {
    setTenant(tenantId)
  }, [tenantId])

  const value = useMemo<TenantContextValue>(
    () => ({ tenantId, setTenantId }),
    [tenantId, setTenantId],
  )

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the active tenant ID and a setter.
 * Must be called inside a `<TenantProvider>` subtree.
 *
 * @throws if called outside a TenantProvider (developer mistake — surfaces clearly)
 */
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext)
  if (ctx === null) {
    throw new Error('useTenant must be used inside a <TenantProvider>')
  }
  return ctx
}
